import type { ManifestSession } from "../models";

export interface SessionStats {
  sessions: number;
  meanSuccess: number;
  meanEffort: number;
  meanCost: number;
  totalTokens: number;
  totalDollars: number;
}

export interface DayBucket {
  date: string;
  count: number;
  tokens: number;
  dollars: number;
}

export interface LeaderboardRow {
  key: string;
  count: number;
  meanSuccess: number;
}

export function computeStats(sessions: ManifestSession[]): SessionStats {
  if (sessions.length === 0) {
    return {
      sessions: 0,
      meanSuccess: 0,
      meanEffort: 0,
      meanCost: 0,
      totalTokens: 0,
      totalDollars: 0,
    };
  }
  let success = 0;
  let effort = 0;
  let cost = 0;
  let tokens = 0;
  let dollars = 0;
  for (const s of sessions) {
    success += s.scores.overall_success;
    effort += s.scores.human_effort;
    cost += s.scores.ai_cost;
    tokens += s.scores.raw?.tokens_total ?? 0;
    dollars += s.scores.raw?.dollars_estimate ?? 0;
  }
  const n = sessions.length;
  return {
    sessions: n,
    meanSuccess: Math.round(success / n),
    meanEffort: Math.round(effort / n),
    meanCost: Math.round(cost / n),
    totalTokens: tokens,
    totalDollars: dollars,
  };
}

export function topConditions(
  sessions: ManifestSession[],
  limit?: number,
): { code: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const s of sessions) {
    for (const c of s.top_conditions) {
      counts.set(c.code, (counts.get(c.code) ?? 0) + c.count);
    }
  }
  const result = [...counts.entries()]
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => b.count - a.count);
  return limit !== undefined ? result.slice(0, limit) : result;
}

export function meanField(
  sessions: ManifestSession[],
  field: (s: ManifestSession) => number,
): number {
  if (sessions.length === 0) return 0;
  return Math.round(sessions.reduce((a, s) => a + field(s), 0) / sessions.length);
}

export function bucketByDay(sessions: ManifestSession[], days: number): DayBucket[] {
  const buckets = new Map<string, DayBucket>();
  // Anchor on the most recent session date so we always show a populated window.
  const anchor =
    sessions.length > 0
      ? sessions
          .reduce((m, s) => (s.started_at > m ? s.started_at : m), sessions[0].started_at)
          .slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  const anchorDate = new Date(`${anchor}T12:00:00Z`);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(anchorDate);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    buckets.set(key, { date: key, count: 0, tokens: 0, dollars: 0 });
  }
  for (const s of sessions) {
    const key = s.started_at.slice(0, 10);
    const b = buckets.get(key);
    if (b) {
      b.count++;
      b.tokens += s.scores.raw?.tokens_total ?? 0;
      b.dollars += s.scores.raw?.dollars_estimate ?? 0;
    }
  }
  return [...buckets.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export function leaderboard(
  sessions: ManifestSession[],
  getKey: (s: ManifestSession) => string,
): LeaderboardRow[] {
  const bins = new Map<string, { count: number; success: number }>();
  for (const s of sessions) {
    const key = getKey(s);
    const b = bins.get(key) ?? { count: 0, success: 0 };
    b.count++;
    b.success += s.scores.overall_success;
    bins.set(key, b);
  }
  return [...bins.entries()]
    .map(([key, v]) => ({ key, count: v.count, meanSuccess: Math.round(v.success / v.count) }))
    .sort((a, b) => b.count - a.count);
}
